import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import debounce from "lodash/debounce";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const CustomTextField = ({
  value: propValue,
  onChange: propOnChange,
  onBlur,
  maxInputLength,
  rule,
  id,
  disabled,
  backgroundColor,
  icon,
  iconLabel,
  visibilityColor,
  width,
  iconWidth,
  placeholder,
  type,
  onSubmit,
  isFullWidth = true,
  ...props
}) => {
  useEffect(() => {
    setValue(propValue);
  }, [propValue]);

  const inputRef = useRef(null);
  const [value, setValue] = useState(propValue);
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Sync prop value if it changes
  useEffect(() => {
    setValue(propValue || "");
  }, [propValue]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    if (input.value && input.value !== value) {
      setValue(input.value);
      propOnChange?.({ target: { value: input.value } });
    }

    const observer = new MutationObserver(() => {
      const currentValue = input.value;
      if (currentValue !== value) {
        setValue(currentValue);
        propOnChange?.({ target: { value: currentValue } });
      }
    });

    observer.observe(input, {
      attributes: true,
      attributeFilter: ["value"],
    });

    return () => observer.disconnect();
  }, []);

  const handleValidation = debounce((value) => {
    if (rule) {
      const { isValid, errorMessage } = rule(value);
      setError(!isValid);
      setHelperText(errorMessage);
    }
  }, 300);

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && onSubmit) {
      onSubmit();
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
    propOnChange({ target: { value } });
    handleValidation(value);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <FormControl fullWidth={isFullWidth} sx={{ width: { width } }}>
      <TextField
        inputRef={inputRef}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        error={error}
        onKeyDown={handleKeyDown}
        helperText={helperText}
        disabled={disabled}
        placeholder={placeholder}
        variant={disabled ? "filled" : "outlined"}
        type={showPassword ? "text" : type}
        sx={{
          ...(disabled && {
            "& .MuiFilledInput-root:before": {
              borderBottom: "none !important",
            },
            "& .MuiFilledInput-root:after": {
              borderBottom: "none !important",
            },
            "& .MuiFilledInput-root:hover:before": {
              borderBottom: "none !important",
            },
            "& .MuiFilledInput-input": {
              transform: "translate(5px, -7px)",
            },
          }),
          "& .MuiOutlinedInput-root, & .MuiFilledInput-root": {
            minHeight: { xs: "44px", sm: "54px" },
          },
          "& .MuiOutlinedInput-input, & .MuiFilledInput-input": {
            fontSize: { xs: "13px", sm: "15px" },
            padding: { xs: "12px 14px !important", sm: "16.5px 14px !important" },
          },
        }}
        InputProps={{
          style: {
            borderRadius: 20,
            backgroundColor: disabled ? backgroundColor : "white",
          },
          inputProps: {
            maxLength: maxInputLength,
          },
          startAdornment: icon ? (
            <InputAdornment
              position="start"
              sx={{
                gap: 1,
                alignItems: "center",
                backgroundColor: "#dbdbdb",
                marginLeft: "-13px",
                // `height:"100%"` doesn't resolve on an auto-height flex row,
                // so use alignSelf:stretch instead — the actual flexbox
                // mechanism for a child to fill the row's cross size — and
                // neutralize MUI's default InputAdornment maxHeight:2em,
                // which was capping it below the input's own height on
                // mobile once padding shrunk.
                alignSelf: "stretch",
                height: "auto",
                maxHeight: "none",
                paddingLeft: { xs: "10px", sm: "12px" },
                paddingRight: { xs: "10px", sm: "12px" },
                borderRadius: "20px 0 0 20px",
                // Icon-only on phone-size screens — the label + its fixed
                // width only apply from `sm` up.
                ...(iconWidth && {
                  width: { xs: "auto", sm: "150px" },
                  minWidth: { xs: "auto", sm: "150px" },
                  flexShrink: 0,
                }),
              }}
            >
              {icon}
              {iconLabel && (
                <Box
                  component="span"
                  sx={{ fontSize: 14, color: "#555", display: { xs: "none", sm: "inline" } }}
                >
                  {iconLabel}
                </Box>
              )}
            </InputAdornment>
          ) : null,

          endAdornment:
            type === "password" ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  sx={{ color: visibilityColor || "inherit" }}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
        fullWidth
        {...props}
      />
    </FormControl>
  );
};

export default CustomTextField;
